import React, { useState } from "react";
import { Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  InputLabel,
  TextField,
  Tooltip,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import dayjs from "dayjs";
import { debounce } from "lodash";
import { TextInputComponent } from "../../../../../common/components";
import DatePickerComponent from "../../../../../common/components/date-picker/date-picker-compoenent";
import CloseIcon from "../../../../../assets/icons/closeIcon.svg";
import infoIcon from "../../../../../assets/icons/info-icon.svg";
import { Close } from "@mui/icons-material";
import "./company-dashboard-initiative-drawer-side-bar.scss";
import { getInitiativeTitle } from "../../../../../api/title-search";
export interface StatusOption {
  statusId: number;
  statusName: string;
}
const CompanyDashboardInitiativeDrawerSideBarDetailsPopup: any = (
  props: any,
) => {
  const {
    isShowEditDetails,
    setIsShowEditDetails,
    detailsData,
    setDetailsData,
    handleCreateInitiative,
    initiativeId,
    payloadData,
    statusListInitiatives,
  } = props;

  const [titleSuggestions, setTitleSuggestions] = useState<any[]>([]);
  const [loadingTitles, setLoadingTitles] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [autocompleteOpen, setAutocompleteOpen] = useState(false);

  // Clear search state when popup closes
  React.useEffect(() => {
    if (!isShowEditDetails) {
      setTitleSuggestions([]);
      setHasSearched(false);
      setAutocompleteOpen(false);
      setLoadingTitles(false);
    }
  }, [isShowEditDetails]);

  // Debounced function to search for initiative titles
  const searchInitiativeTitles = React.useMemo(
    () =>
      debounce(async (searchValue: string) => {
        if (!searchValue || searchValue.trim().length < 2) {
          setTitleSuggestions([]);
          setHasSearched(false);
          return;
        }

        setLoadingTitles(true);
        setHasSearched(true);
        try {
          const response = await getInitiativeTitle({ title: searchValue });
          const initiatives = response?.data?.data || [];
          setTitleSuggestions(initiatives);
        } catch (error: any) {
          console.error("Error fetching initiative titles:", error);
          setTitleSuggestions([]);
        } finally {
          setLoadingTitles(false);
        }
      }, 500),
    [],
  );

  const validationSchema = Yup.object({
    weightage: Yup.number()
      .min(0, "Weightage cannot be negative")
      .max(100, "Weightage cannot exceed 100"),
    name: Yup.string().required("Title is required"),
    threshold: Yup.number()
      .required("Threshold is required")
      .min(0, "Threshold cannot be negative")
      .max(100, "Threshold cannot exceed 100"),
  });

  const tooltipText = (values: any) => {
    return values.name.length < 2
      ? "Type at least 2 characters"
      : "No results found.";
  };

  const slotsPaperTitle = (props: any, values: any) => {
    const showNoResults =
      !loadingTitles &&
      hasSearched &&
      titleSuggestions.length === 0 &&
      values.name.trim().length >= 2;

    const showLoading = loadingTitles;

    const showEmptyState = titleSuggestions.length === 0;

    const renderContent = () => {
      if (showLoading) {
        return (
          <div
            style={{
              padding: "8px 16px",
              color: "#666",
              fontSize: "14px",
              textAlign: "center",
            }}
          >
            Searching...
          </div>
        );
      }

      if (showNoResults) {
        return (
          <div
            style={{
              padding: "8px 16px",
              color: "#666",
              fontSize: "14px",
              textAlign: "center",
            }}
          >
            No results found.
          </div>
        );
      }

      if (showEmptyState) {
        return (
          <div
            style={{
              padding: "8px 16px",
              color: "#666",
              fontSize: "14px",
              textAlign: "center",
            }}
          >
            Search
          </div>
        );
      }

      return props.children;
    };

    return (
      <div
        {...props}
        style={{
          backgroundColor: "#fff",
          boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 8px",
            background: "#fff",
            borderBottom: "0.5px solid #eee",
          }}
        >
          <div className="" style={{ fontSize: "14px" }}>
            <InputLabel style={{ fontSize: "14px" }}>
              Existing Initiatives
            </InputLabel>
          </div>
          <button
            style={{
              border: "none",
              background: "none",
              padding: 0,
              margin: 0,
              cursor: "pointer",
            }}
            onClick={(e) => {
              e.stopPropagation();
              setTitleSuggestions([]);
              setHasSearched(false);
              setAutocompleteOpen(false);
            }}
          >
            <img
              alt="Close"
              src={CloseIcon}
              style={{
                cursor: "pointer",
                color: "#555",
                margin: "4px 0",
              }}
            />
          </button>
        </div>
        {renderContent()}
      </div>
    );
  };
  const slotsPaperStatus = (props: any, values: any) => {
    return (
      <div
        {...props}
        style={{
          backgroundColor: "#fff",
          boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            padding: "4px 8px",
            background: "#fff",
          }}
        >
          <Close
            sx={{
              cursor: "pointer",
              color: "#555",
              margin: "4px 0",
              "&:hover": { color: "#000" },
            }}
            onClick={(e) => {
              e.stopPropagation();
              const dropdown = document.querySelector(
                ".MuiAutocomplete-popper",
              ) as HTMLElement;
              if (dropdown) dropdown.style.display = "none";
            }}
          />
        </div>

        {props.children}
      </div>
    );
  };
  return (
    <Dialog
      open={isShowEditDetails}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      onClose={(event, reason) => {
        if (reason === "backdropClick") {
          return;
        }
        setIsShowEditDetails(false);
      }}
      className={`company-dashboard-initiative-drawer-side-bar-details-popup `}
    >
      <DialogTitle id="alert-dialog-title">
        <div className="header-wrapper">
          <div className="header-wrapper-text">
            {initiativeId ? "Edit Initiative Name" : "Add Initiative Name"}
          </div>
          <div className="image-wrapper">
            <button
              onClick={() => setIsShowEditDetails(false)}
              style={{
                border: "none",
                background: "none",
                padding: 0,
                cursor: "pointer",
              }}
            >
              <img
                alt="Close"
                src={CloseIcon}
                className="close-icon"
                style={{ cursor: "pointer" }}
              />
            </button>
          </div>
        </div>
      </DialogTitle>
      <DialogContent>
        <Formik
          initialValues={{
            name: detailsData?.name || "",
            lastUpdated: dayjs(detailsData?.lastUpdated) || dayjs(),
            weightage: detailsData?.weightage || "0.00",
            progress: detailsData?.progress || 0,
            threshold: detailsData?.threshold || 15,
            status:
              statusListInitiatives.find(
                (s: StatusOption) =>
                  s.statusName === detailsData?.status || null,
              ) ||
              statusListInitiatives.find(
                (s: StatusOption) => s.statusName === "Not Started",
              ) ||
              null,
          }}
          validationSchema={validationSchema}
          validateOnChange={true}
          onSubmit={(values: any) => {
            const newData = {
              ...values,
            };
            setDetailsData(() => ({
              ...newData,
            }));
            handleCreateInitiative(setIsShowEditDetails, {
              ...payloadData,
              title: values?.name,
              weightage: values?.weightage || 0,
              threshold: values?.threshold || 0,
              status: values?.status?.statusName || "Not Started",
            });
          }}
        >
          {({ values, setFieldValue, isValid, submitCount }) => (
            <Form>
              <div className="company-dashboard-initiative-drawer-side-bar-details-popup-edit">
                <div className="company-dashboard-initiative-drawer-side-bar-details-popup-edit-content">
                  <div className="form-group form-text-input-component">
                    <InputLabel>Title</InputLabel>
                    <Autocomplete
                      freeSolo
                      options={titleSuggestions}
                      getOptionLabel={(option: any) =>
                        typeof option === "string" ? option : option.title || ""
                      }
                      loading={loadingTitles}
                      filterOptions={(x) => x}
                      open={autocompleteOpen}
                      onOpen={() => {
                        // Only open if there's meaningful content to show
                        if (
                          values.name.trim().length >= 2 ||
                          titleSuggestions.length > 0 ||
                          loadingTitles
                        ) {
                          setAutocompleteOpen(true);
                        }
                      }}
                      onClose={(event, reason) => {
                        // Always close on blur (clicking outside) or escape key
                        if (reason === "blur" || reason === "escape") {
                          setAutocompleteOpen(false);
                          return;
                        }

                        // For other reasons, keep open if there are results, loading, or no results after search
                        const shouldStayOpen =
                          titleSuggestions.length > 0 ||
                          loadingTitles ||
                          (hasSearched &&
                            !loadingTitles &&
                            values.name.trim().length >= 2);

                        // Only close if shouldn't stay open (not when showing no results)
                        if (!shouldStayOpen) {
                          setAutocompleteOpen(false);
                        }
                      }}
                      disableClearable
                      value={values.name}
                      inputValue={values.name}
                      onInputChange={(event, newInputValue, reason) => {
                        if (reason === "input") {
                          setFieldValue("name", newInputValue);
                          searchInitiativeTitles(newInputValue);
                          // Open dropdown when typing with enough characters
                          if (newInputValue.trim().length >= 2) {
                            setAutocompleteOpen(true);
                          } else {
                            // Close dropdown when text is cleared or too short
                            setAutocompleteOpen(false);
                            setTitleSuggestions([]);
                            setHasSearched(false);
                          }
                        }
                      }}
                      onChange={(event) => {
                        // Prevent selection - only allow typing
                        event.preventDefault();
                      }}
                      onBlur={() => {
                        // Only clear if we're not showing no results message
                        setTimeout(() => {
                          const shouldKeepOpen =
                            titleSuggestions.length > 0 ||
                            loadingTitles ||
                            (hasSearched &&
                              !loadingTitles &&
                              values.name.trim().length >= 2);

                          if (!shouldKeepOpen) {
                            setTitleSuggestions([]);
                            setHasSearched(false);
                            setAutocompleteOpen(false);
                          }
                        }, 200);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Enter Title"
                          slotProps={{
                            htmlInput: {
                              ...params.inputProps,
                              maxLength: 150,
                            },
                          }}
                        />
                      )}
                      renderOption={(props, option: any) => (
                        <Tooltip title="Selection is turned off for this field.">
                          <li
                            {...props}
                            style={{
                              cursor: "not-allowed",
                              padding: "8px 16px",
                              opacity: 0.7,
                              backgroundColor:
                                values?.name?.trim()?.toLowerCase() ===
                                option.title?.trim()?.toLowerCase()
                                  ? "#ddddfa"
                                  : "transparent !important",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor =
                                values?.name?.trim()?.toLowerCase() ===
                                option.title?.trim()?.toLowerCase()
                                  ? "#ddddfa"
                                  : "transparent !important";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor =
                                values?.name?.trim()?.toLowerCase() ===
                                option.title?.trim()?.toLowerCase()
                                  ? "#ddddfa"
                                  : "transparent !important";
                            }}
                          >
                            <div
                              style={{
                                fontSize: "14px",
                                pointerEvents: "none",
                              }}
                            >
                              {option.title}
                            </div>
                          </li>
                        </Tooltip>
                      )}
                      noOptionsText={
                        loadingTitles ? "Searching..." : tooltipText(values)
                      }
                      slotProps={{
                        listbox: {
                          style: {
                            maxHeight: "200px",
                          },
                        },
                      }}
                      slots={{
                        paper: (props) => slotsPaperTitle(props, values),
                      }}
                    />
                    <ErrorMessage
                      name="title"
                      component="div"
                      className="error-message"
                    />
                  </div>
                  <div className="form-group">
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        marginBottom: "8px",
                      }}
                    >
                      <InputLabel style={{ margin: 0 }}>Threshold</InputLabel>
                      <Tooltip
                        title="The maximum lead time allowed (in %age) before an Initiative is marked delayed."
                        arrow
                        placement="right-start"
                      >
                        <img
                          src={infoIcon}
                          alt="info"
                          style={{
                            width: "16px",
                            height: "16px",
                            cursor: "pointer",
                          }}
                        />
                      </Tooltip>
                    </div>
                    <TextInputComponent
                      type="number"
                      fieldName="threshold"
                      label=""
                      placeholder="0.00"
                      optional={false}
                      disabled={false}
                      value={values.threshold}
                      onChange={(e: any) => {
                        if (
                          e?.target?.value === "" ||
                          /^\d+$/.test(e?.target?.value)
                        ) {
                          setFieldValue("threshold", e?.target?.value);
                        }
                      }}
                      isLocked={true}
                      blockInvalidChars={true}
                      lockedIcons={
                        <i
                          className="fa fa-percent"
                          style={{
                            color: "#0000008a",
                            fontWeight: "normal",
                          }}
                        />
                      }
                    />
                  </div>
                  <div className="form-group">
                    <DatePickerComponent
                      fieldName={"lastUpdated"}
                      label="Created Date"
                      value={values?.lastUpdated}
                      disabled={true}
                      onChange={(e: any) => {
                        setFieldValue("lastUpdated", e);
                      }}
                    />
                  </div>
                  <div className="form-group form-group-status form-text-input-component">
                    <InputLabel>Status</InputLabel>

                    <Autocomplete<StatusOption>
                      value={values.status}
                      onChange={(event, newValue) => {
                        setFieldValue("status", newValue);
                      }}
                      options={statusListInitiatives}
                      getOptionLabel={(option) => option.statusName}
                      isOptionEqualToValue={(option, value) =>
                        option.statusId === value.statusId
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label=""
                          placeholder="Select Status"
                        />
                      )}
                      sx={{
                        "& .MuiAutocomplete-option": {
                          fontSize: "14px",
                          fontFamily: "Manrope, sans-serif",
                        },
                      }}
                      slots={{
                        paper: (props) => slotsPaperStatus(props, values),
                      }}
                      slotProps={{
                        popper: {
                          sx: {
                            "& .MuiAutocomplete-option": {
                              fontSize: "14px",
                              fontFamily: "Manrope, sans-serif",
                            },
                          },
                        },
                      }}
                    />
                  </div>
                </div>
                <div className="company-dashboard-initiative-drawer-side-bar-details-popup-edit-action">
                  <button
                    className="company-dashboard-initiative-drawer-side-bar-details-popup-edit-action-cancel"
                    onClick={() => setIsShowEditDetails(false)}
                    type="button"
                  >
                    Cancel
                  </button>
                  <button
                    className="company-dashboard-initiative-drawer-side-bar-details-popup-edit-action-save"
                    type="submit"
                    disabled={submitCount > 0 && !isValid}
                    style={{
                      opacity: submitCount > 0 && !isValid ? 0.5 : 1,
                      cursor:
                        submitCount > 0 && !isValid ? "not-allowed" : "pointer",
                    }}
                  >
                    {initiativeId ? "Save" : "Add"}
                  </button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default CompanyDashboardInitiativeDrawerSideBarDetailsPopup;
